"use client";

import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

interface AddNewLanguageProps {
  goToNextStep: () => void;
}

export default function AddNewLanguage({ goToNextStep }: AddNewLanguageProps) {
  const { t } = useTranslation(["common", "profile"]);

  return (
    <ScrollArea className="h-full w-full">
      <div className="mx-auto w-full max-w-7xl p-4 sm:p-6">
        {/* Title and description */}
        <div className="mb-6 text-center">
          <h1 className="text-accent-foreground mb-2 text-2xl font-bold sm:text-3xl">
            {t("profile:addNewLanguage.title")}
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            {t("profile:addNewLanguage.description")}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Left side: colorful cards (like in onboarding) */}
          <div className="hidden lg:col-span-1 lg:flex lg:flex-col lg:justify-center lg:space-y-4">
            {[0, 1, 2, 3].map((index) => (
              <motion.div
                key={index}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-foreground/30 relative overflow-hidden rounded-xl p-4 text-start backdrop-blur-sm"
                initial={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.2, delay: 0.05 * index }}
              >
                <div
                  className={`absolute top-1/2 left-0 h-full w-full -translate-x-1/6 -translate-y-1/2 rounded-full bg-gradient-to-br blur-3xl transition-colors duration-700 ${
                    index === 0
                      ? "from-blue-500/20 to-cyan-500/20"
                      : index === 1
                        ? "from-purple-500/20 to-pink-500/20"
                        : index === 2
                          ? "from-emerald-500/20 to-green-500/20"
                          : "from-orange-500/20 to-amber-500/20"
                  }`}
                />
                <div className="relative z-50 flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm leading-tight font-semibold">
                      {index === 0
                        ? t("profile:addNewLanguage.chooseNewLanguage")
                        : index === 1
                          ? t("profile:addNewLanguage.customizeNewProfile")
                          : index === 2
                            ? t("profile:addNewLanguage.startLearning")
                            : t("profile:addNewLanguage.expandLanguageCapabilities")}
                    </h3>
                    <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                      {index === 0
                        ? t("profile:addNewLanguage.chooseNewLanguageDesc")
                        : index === 1
                          ? t("profile:addNewLanguage.customizeNewProfileDesc")
                          : index === 2
                            ? t("profile:addNewLanguage.startLearningDesc")
                            : t("profile:addNewLanguage.expandLanguageCapabilitiesDesc")}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Right side: illustration and CTA */}
          <div className="col-span-1 flex items-start justify-center">
            <div className="w-full max-w-3xl space-y-4 sm:space-y-6">
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="relative overflow-hidden rounded-xl max-sm:hidden"
                initial={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  alt={t("profile:getStarted.illustrationAlt", "Profile setup illustration")}
                  className="h-auto w-full object-contain select-none max-lg:max-h-80"
                  src={"https://public-assets.ximly.app/tutor-profile-help.webp"}
                />
              </motion.div>

              {/* Mobile tips under the image */}
              <div className="lg:hidden">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {[0, 1, 2, 3].map((index) => (
                    <motion.div
                      key={`m-tip-${index}`}
                      animate={{ opacity: 1, scale: 1 }}
                      className="bg-foreground/30 relative overflow-hidden rounded-xl p-4 text-start backdrop-blur-sm"
                      initial={{ opacity: 0, scale: 0.98 }}
                      transition={{ duration: 0.2, delay: 0.05 * index }}
                    >
                      <div
                        className={`absolute top-1/2 left-0 h-full w-full -translate-x-1/6 -translate-y-1/2 rounded-full bg-gradient-to-br blur-3xl transition-colors duration-700 ${
                          index === 0
                            ? "from-blue-500/20 to-cyan-500/20"
                            : index === 1
                              ? "from-purple-500/20 to-pink-500/20"
                              : index === 2
                                ? "from-emerald-500/20 to-green-500/20"
                                : "from-orange-500/20 to-amber-500/20"
                        }`}
                      />
                      <div className="relative z-50 flex items-start gap-3">
                        <div className="min-w-0 flex-1">
                          <h3 className="text-sm leading-tight font-semibold">
                            {index === 0
                              ? t("profile:addNewLanguage.chooseNewLanguage")
                              : index === 1
                                ? t("profile:addNewLanguage.customizeNewProfile")
                                : index === 2
                                  ? t("profile:addNewLanguage.startLearning")
                                  : t("profile:addNewLanguage.expandLanguageCapabilities")}
                          </h3>
                          <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                            {index === 0
                              ? t("profile:addNewLanguage.chooseNewLanguageDesc")
                              : index === 1
                                ? t("profile:addNewLanguage.customizeNewProfileDesc")
                                : index === 2
                                  ? t("profile:addNewLanguage.startLearningDesc")
                                  : t("profile:addNewLanguage.expandLanguageCapabilitiesDesc")}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="col-span-2 mx-auto w-full">
            <Button
              className="group w-full px-8 py-6 text-lg"
              size="lg"
              variant="foreground"
              onClick={goToNextStep}
            >
              {t("common:getStarted")}
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </div>
      </div>
    </ScrollArea>
  );
}
